import { useState } from 'react';
import styled from 'styled-components';

import { authorization } from '../../../api';
import { Input } from '../../../atoms';
import CustomMadal from '../../../atoms/Modal/Modal';
import SolidButton from '../../../atoms/SolidButton';
import {
  handleInputBlur,
  handleInputChange,
  isEmptyInput,
} from '../../../helpers';
import { useNotification } from '../../../hooks';
import MuiInputPassword from '../../../molecules/MuiInputPassword';
import { Colors, Fonts, rem } from '../../../themes';

type IChangePasswordProps = {
  showPasswordModal: boolean;
  setShowPasswordModal: (value: boolean) => void;
};

function ChangePasswordModal({
  showPasswordModal,
  setShowPasswordModal,
}: IChangePasswordProps) {
  const { showError, showSuccess } = useNotification();

  const [oldPassword, setOldPassword] = useState({ value: '', errorMsg: '' });
  const [newPassword1, setNewPassword1] = useState({ value: '', errorMsg: '' });
  const [newPassword2, setNewPassword2] = useState({ value: '', errorMsg: '' });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const validateChangePassword = () => {
    let valid = true;

    if (isEmptyInput(oldPassword, setOldPassword, 'Enter current password'))
      valid = false;
    if (isEmptyInput(newPassword1, setNewPassword1, 'Enter new password'))
      valid = false;
    if (isEmptyInput(newPassword2, setNewPassword2, 'Confirm new password'))
      valid = false;

    if (newPassword1.value !== newPassword2.value) {
      setNewPassword2(prev => ({
        ...prev,
        errorMsg: 'Passwords do not match',
      }));
      valid = false;
    }

    return valid;
  };

  const handleChangePassword = () => {
    if (!validateChangePassword()) return;

    setIsChangingPassword(true);

    authorization
      .changePassword({
        oldPassword: oldPassword.value,
        newPassword1: newPassword1.value,
        newPassword2: newPassword2.value,
      })
      .then(() => {
        showSuccess('Password changed successfully');
        setShowPasswordModal(false);

        setOldPassword({ value: '', errorMsg: '' });
        setNewPassword1({ value: '', errorMsg: '' });
        setNewPassword2({ value: '', errorMsg: '' });
      })
      .catch(({ data }) => {
        if (data?.oldPassword)
          setOldPassword({ ...oldPassword, errorMsg: data.oldPassword[0] });
        if (data?.newPassword1)
          setNewPassword1({ ...newPassword1, errorMsg: data.newPassword1[0] });

        showError('Failed to change password');
      })
      .finally(() => setIsChangingPassword(false));
  };

  return (
    <CustomMadal
      modalIsOpen={showPasswordModal}
      setModalIsOpen={setShowPasswordModal}
      disabledOverlayClick
    >
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>Change Password</ModalTitle>
          <ModalSubtitle>
            Enter your current and new password to update your password.
          </ModalSubtitle>
        </ModalHeader>

        <ModalContent>
          <FullWidthInputWrapper>
            <MuiInputPassword
              value={oldPassword.value}
              onChange={handleInputChange(setOldPassword)}
              onBlur={handleInputBlur(oldPassword, setOldPassword)}
              placeholder="Enter current password"
              error={oldPassword.errorMsg}
            />
          </FullWidthInputWrapper>

          <FullWidthInputWrapper>
            <MuiInputPassword
              value={newPassword1.value}
              onChange={handleInputChange(setNewPassword1)}
              onBlur={handleInputBlur(newPassword1, setNewPassword1)}
              placeholder="Enter new password"
              error={newPassword1.errorMsg}
            />
          </FullWidthInputWrapper>

          <FullWidthInputWrapper>
            <MuiInputPassword
              value={newPassword2.value}
              onChange={handleInputChange(setNewPassword2)}
              onBlur={handleInputBlur(newPassword2, setNewPassword2)}
              placeholder="Re-enter confirm password"
              error={newPassword2.errorMsg}
            />
          </FullWidthInputWrapper>
        </ModalContent>

        <ModalActions>
          <CancelButton
            text="Cancel"
            onClick={() => setShowPasswordModal(false)}
          />

          <ConfirmButton
            text={isChangingPassword ? 'Updating...' : 'Change Password'}
            onClick={handleChangePassword}
            disabled={isChangingPassword}
          />
        </ModalActions>
      </ModalContainer>
    </CustomMadal>
  );
}

export const RowWrapper = styled.div`
  display: flex;
  margin-top: 10px;
  flex-wrap: wrap;
  justify-content: space-between;
`;

export const TopContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Text = styled.div`
  color: ${Colors.Black};
  font-family: ${Fonts.Sofia};
  font-style: normal;
  font-weight: 300;
  line-height: normal;
`;

export const InputLabel = styled(Text)`
  font-size: ${rem(11.25)};
  margin-bottom: 6px;
`;

export const InputValue = styled.div`
  padding: 7px;
  color: ${Colors.LightSecondGrey};
  border: 0.5px solid ${Colors.WeakGrey};
  border-radius: 6px;
  width: 100%;
  font-size: ${rem(11)};
  font-family: ${Fonts.Sofia};
`;

export const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  & > *:not(:first-child) {
    font-size: ${rem(8)};
  }
`;

export const StyledInput = styled(Input)`
  font-size: ${rem(11)};
  padding: 7px;
  overflow: hidden;
  text-overflow: ellipsis;
  border-radius: 6px;
  height: 52px;
  color: ${Colors.LightSecondGrey};
  ::placeholder {
    color: ${Colors.LightSecondGrey};
  }
`;

export const EditButtons = styled.div`
  display: flex;
  width: fit-content;
  margin: 31px auto 0px;

  & > *:not(:last-child) {
    margin-right: 10px;
  }
`;

export const StyledButton = styled(SolidButton)`
  background-color: ${Colors.DarkBlue};
  color: ${Colors.White};
  font-size: ${rem(11.25)};
  height: auto;
  width: auto;
  padding: 13px 41px;
`;

export const CancelButton = styled(SolidButton)`
  background-color: ${Colors.PaleAquaBlue};
  color: ${Colors.Black};
  font-size: ${rem(11.25)};
  padding: 13px 41px;
  width: auto;
  height: auto;
`;

const ModalContainer = styled.div`
  padding: 30px;
  min-width: 400px;
  max-width: 500px;
`;

const ModalHeader = styled.div`
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  font-family: ${Fonts.Sofia};
  font-size: ${rem(18)};
  font-weight: 600;
  color: ${Colors.Black};
  margin: 0 0 10px 0;
`;

const ModalSubtitle = styled.p`
  font-family: ${Fonts.Sofia};
  font-size: ${rem(14)};
  color: ${Colors.LightSecondGrey};
  margin: 0;
  line-height: 1.4;
`;

const ModalContent = styled.div`
  margin-bottom: 25px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ConfirmButton = styled(SolidButton)`
  background-color: ${Colors.DarkRed};
  color: ${Colors.White};
  font-size: ${rem(11.25)};
  padding: 13px 41px;
  width: auto;
  height: auto;

  &:disabled {
    background-color: ${Colors.LightSecondGrey};
    cursor: not-allowed;
  }
`;

const FullWidthInputWrapper = styled.div`
  width: 100%;
  margin-bottom: 20px;

  display: flex;

  & > div {
    flex: 1;
  }
`;

export default ChangePasswordModal;
